import React, { useState, useEffect, useRef } from 'react';
import { Text, Rect, Group } from 'react-konva';

/**
 * Unified Drawing Text Component for CAD/Professional drawing aesthetics.
 * Centralizes font styling, crisp scaling, and optional background masks (wipeouts).
 */
const DrawingText = ({
  text,
  x = 0,
  y = 0,
  fontSize = 12, //this font size is being completley ignored
  fontFamily = 'Arial, "Courier New", Monaco, monospace',
  fontStyle = 'bold',
  fill = '#000000',
  rotation = 0,
  offsetX = 0,
  offsetY = 0,
  align = 'center',
  verticalAlign = 'middle',
  listening = false,
  useBadge = true,
  badgeStroke = null,
  badgeFill = 'rgba(255, 255, 255, 0.7)',
  stageScale = 1,
  onDblClick = null,
  onDblTap = null,
  visible = true,
  textRef: externalTextRef = null,
  width = null,
  wrap = null,
  opacity = 1,
  centerAlign = null,
}) => {
  const localTextRef = useRef(null);
  const textNodeRef = externalTextRef || localTextRef;

  // 1. Initial size estimation for zero-flicker mounting
  const getEstimatedSize = () => {
    const lines = String(text || '').split('\n');
    const maxLineLen = Math.max(...lines.map((l) => l.length), 0);
    // Monospace aspect ratio is typically ~0.6
    const estWidth = width || maxLineLen * fontSize * 0.6;
    const estHeight = lines.length * fontSize * 1.25;
    return { width: estWidth, height: estHeight };
  };

  const [measuredSize, setMeasuredSize] = useState(getEstimatedSize);

  // 2. Measure actual text width/height after mounting/updates
  useEffect(() => {
    if (textNodeRef.current) {
      setMeasuredSize({
        width: textNodeRef.current.width(),
        height: textNodeRef.current.height(),
      });
    }
  }, [text, fontSize, width, textNodeRef]);

  if (!visible) return null;

  // 3. Layout params
  const paddingX = Math.max(2, 2 / stageScale);
  const paddingY = Math.max(1, 2 / stageScale);
  const cornerRadius = Math.max(1, 2 / stageScale);
  const strokeWidth = Math.max(0.5, 1 / stageScale);

  const isCentered = centerAlign !== null ? centerAlign : (align === 'center');

  // If there's no badge and no rotation, render the raw Konva Text node directly.
  // This preserves ref positioning and drag anchor behaviors for text displacement/dragging in TextLabelShape.js
  if (!useBadge && rotation === 0) {
    return (
      <Text
        ref={textNodeRef}
        x={x}
        y={y}
        offsetX={isCentered ? measuredSize.width / 2 : offsetX}
        offsetY={isCentered ? measuredSize.height / 2 : offsetY}
        text={text}
        fontSize={fontSize}
        fontFamily={fontFamily}
        fontStyle={fontStyle}
        fill={fill}
        align={align}
        verticalAlign={verticalAlign}
        listening={listening || !!onDblClick || !!onDblTap}
        onDblClick={onDblClick}
        onDblTap={onDblTap}
        width={width}
        wrap={wrap}
        opacity={opacity}
      />
    );
  }

  const textOffsetX = isCentered ? measuredSize.width / 2 : 0;
  const textOffsetY = isCentered ? measuredSize.height / 2 : 0;
  const rectX = isCentered ? -measuredSize.width / 2 - paddingX : -paddingX;
  const rectY = isCentered ? -measuredSize.height / 2 - paddingY : -paddingY;

  // If text is rotated, we wrap the whole assembly in a Group so the background rotated with it
  const textElement = (
    <Text
      ref={textNodeRef}
      x={0}
      y={0}
      offsetX={textOffsetX}
      offsetY={textOffsetY}
      text={text}
      fontSize={fontSize}
      fontFamily={fontFamily}
      fontStyle={fontStyle}
      fill={fill}
      align={align}
      verticalAlign={verticalAlign}
      listening={listening || !!onDblClick || !!onDblTap}
      onDblClick={onDblClick}
      onDblTap={onDblTap}
      width={width}
      wrap={wrap}
      opacity={opacity}
    />
  );

  return (
    <Group
      x={x}
      y={y}
      rotation={rotation}
      offsetX={offsetX}
      offsetY={offsetY}
      listening={listening || !!onDblClick || !!onDblTap}
    >
      {/* Background Mask (Wipeout) */}
      {useBadge && (
        <Rect
          x={rectX}
          y={rectY}
          width={measuredSize.width + paddingX * 2}
          height={measuredSize.height + paddingY * 2}
          fill={badgeFill}
          stroke={badgeStroke || fill}
          strokeWidth={strokeWidth}
          cornerRadius={cornerRadius}
          listening={false}
          opacity={opacity}
        />
      )}
      {textElement}
    </Group>
  );
};

export default DrawingText;
